/**
 * "Nova consulta": agendar no SISAC numa tela só, em três passos (#1011; fase
 * 2 da agenda da clínica, 24/10).
 *
 * 1. Especialidade e médico; "Ver vagas" confirma e vira uma linha com "Alterar".
 * 2. O dia e o horário no calendário; "Continuar" confirma a vaga.
 * 3. Os dados do agendamento, como no "Horário Vago" do SISAC: o paciente
 *    (vinculado, procurado ou sem cadastro), local, origem, tipo, nascimento,
 *    CEP, convênio, plano e o resto. "Agendar consulta" é o único primário.
 *
 * ── Só tela ──────────────────────────────────────────────────────────────
 *
 * O que a pessoa digita e escolhe mora aqui: os campos, a vaga, o formulário.
 * O que depende do SISAC chega pronto por props (o paciente, as vagas, as
 * listas, o envio), e cada pedido sai por um `ao…`.
 *
 * ── Um clique só ─────────────────────────────────────────────────────────
 *
 * Enquanto o envio não volta, tudo trava: um segundo clique daria "vaga
 * ocupada" com a primeira marcação feita.
 *
 * ── Duas formas ──────────────────────────────────────────────────────────
 *
 * `cartoes` (o card da oportunidade): as três etapas empilhadas, a feita vira
 * uma linha com "Alterar". `passos` (o painel do Atendimento, 25/10): uma
 * etapa por vez, com a trilha 1-2-3 no topo e "Voltar" embaixo, para a pessoa
 * não rolar a coluna atrás do próximo campo.
 */

import { useEffect, useState, type ReactNode } from 'react';
import type { AgendamentoDaConsulta, PadroesDaAgenda } from '@cliqcrm/contracts';
import {
  Botao,
  CalendarioDeVagas,
  CampoEscolha,
  Icone,
  type CalendarioPaginado,
} from '@/shared/ui';
import { Carregando } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  ESPERA_DO_SISAC,
  atende,
  especialidadesDoMedico,
  medicosDaEspecialidade,
  type EstadoDasVagas,
  type EstadoDoEnvio,
  type OpcaoDaAgenda,
  type VagaLivre,
} from '../lib/consultas-na-tela';
import {
  errosDoFormulario,
  convenioFixo,
  formularioDoAgendamentoLido,
  formularioInicial,
  type FormularioDoAgendamento,
} from '../lib/formulario-do-agendamento';
import { instantePorExtenso } from '../lib/marcar-no-sisac';
import type { ListaDoFormulario, PacienteDaConsulta } from '../lib/consultas-na-tela';
import { DadosDoAgendamento } from './dados-do-agendamento';
import { ValorDaConsulta } from './valor-da-consulta';
import './nova-consulta.css';

const SEM_PADROES: PadroesDaAgenda = {};

/** O "Editar" da consulta marcada (#1187), como o modelo o entrega. */
export type Remarcacao = {
  /** A vaga de agora no SISAC. */
  readonly vagaAtual: string;
  readonly consultaEm: string | null;
  /** O médico da vaga de agora: com outro, a vaga não serve. */
  readonly medicoId: string | null;
  readonly dados: AgendamentoDaConsulta['dados'];
  readonly carregando: boolean;
};

export type EscolhaDoMedico = { readonly especialidadeId: string; readonly medicoId: string };

export function NovaConsulta({
  especialidades,
  medicos,
  inicial,
  paciente,
  buscaDoPaciente,
  vagas,
  porMes = null,
  padroes = SEM_PADROES,
  remarcacao = null,
  listas,
  envio,
  forma = 'cartoes',
  noComeco = null,
  telefoneDoContato = '',
  diasDeVagas = 14,
  hoje = new Date(),
  aoVerVagas,
  aoTrocarEspecialidade = () => undefined,
  aoEscolherConvenio,
  aoRecarregarVagas,
  aoMarcar,
  aoCancelar,
}: {
  especialidades: readonly OpcaoDaAgenda[];
  medicos: readonly OpcaoDaAgenda[];
  /** O que a ficha já tem, para os campos não começarem vazios. */
  inicial?: Partial<EscolhaDoMedico>;
  paciente: PacienteDaConsulta;
  /** O bloco que procura e vincula o paciente no SISAC; nulo sem cliente. */
  buscaDoPaciente: ReactNode;
  /** As vagas do médico da última confirmação de "Ver vagas". */
  vagas: EstadoDasVagas;
  /** O calendário por mês (#1187), quando a organização ligou os dias com vaga. */
  porMes?: CalendarioPaginado | null;
  listas: {
    readonly convenios: ListaDoFormulario;
    readonly planos: ListaDoFormulario;
    readonly origens: ListaDoFormulario;
    readonly locais: ListaDoFormulario;
  };
  envio: EstadoDoEnvio;
  /** Os padrões da organização (#1187): local, origem, convênio e plano de partida. */
  padroes?: PadroesDaAgenda;
  /**
   * O "Editar" da consulta marcada (#1187): as mesmas etapas, com o horário de
   * agora como vaga ("Manter o horário atual") e o passo 3 com o gravado.
   */
  remarcacao?: Remarcacao | null;
  /** Etapas empilhadas (o card) ou uma por vez (o painel do Atendimento). */
  forma?: 'cartoes' | 'passos';
  /** O que vem antes do médico na etapa 1: a escolha da oportunidade, no Atendimento. */
  noComeco?: ReactNode;
  /** O telefone do lead (DDD e número): o WhatsApp e o celular começam com ele. */
  telefoneDoContato?: string;
  /** Quantos dias à frente as vagas olham, para o texto de lista vazia (sem o calendário por mês). */
  diasDeVagas?: number;
  hoje?: Date;
  aoVerVagas: (escolha: EscolhaDoMedico) => void;
  /** Trocou a especialidade no passo 3 (#1187): o médico e o horário ficam. */
  aoTrocarEspecialidade?: (especialidadeId: string) => void;
  aoEscolherConvenio: (codigo: string) => void;
  aoRecarregarVagas: () => void;
  aoMarcar: (vagaId: string, formulario: FormularioDoAgendamento) => void;
  aoCancelar: () => void;
}): ReactNode {
  const [especialidadeId, definirEspecialidadeId] = useState(inicial?.especialidadeId ?? '');
  const [medicoId, definirMedicoId] = useState(inicial?.medicoId ?? '');
  const [confirmada, definirConfirmada] = useState<EscolhaDoMedico | null>(null);
  const [alterando, definirAlterando] = useState(false);
  const [vagaId, definirVagaId] = useState<string | null>(null);
  const [vagaConfirmada, definirVagaConfirmada] = useState(false);
  const [tentou, definirTentou] = useState(false);
  const [formulario, definirFormulario] = useState<FormularioDoAgendamento>(() =>
    formularioDoAgendamentoLido(
      formularioInicial({
        dados: paciente.situacao === 'vinculado' ? paciente.dados : null,
        padroes,
        telefoneDoContato,
      }),
      remarcacao?.dados ?? null,
    ),
  );
  const mudar = (mudanca: Partial<FormularioDoAgendamento>) =>
    definirFormulario((atual) => ({ ...atual, ...mudanca }));

  /*
   * Ajustes quando a prop muda, no próprio render (o padrão do React para isso,
   * sem efeito): a vaga tomada no envio deixa de estar escolhida; o paciente
   * vinculado agora preenche o que ainda está vazio, e o convênio dele vence
   * o padrão da organização (#1187).
   */
  const ocupada = vagas.situacao === 'pronta' && vagas.ocupada;
  const [ocupadaVista, definirOcupadaVista] = useState(ocupada);
  if (ocupada !== ocupadaVista) {
    definirOcupadaVista(ocupada);
    if (ocupada) {
      definirVagaId(null);
      definirVagaConfirmada(false);
    }
  }
  const codigoDoVinculado = paciente.situacao === 'vinculado' ? paciente.paciente.codigo : null;
  const [vinculadoVisto, definirVinculadoVisto] = useState(codigoDoVinculado);
  if (codigoDoVinculado !== vinculadoVisto) {
    definirVinculadoVisto(codigoDoVinculado);
    if (paciente.situacao === 'vinculado' && paciente.dados) {
      const doCadastro = formularioInicial({ dados: paciente.dados, telefoneDoContato });
      // O convênio do cadastro vence o padrão da organização, se a pessoa não trocou.
      const convenioDoCadastro = paciente.dados.cod_convenio ?? '';
      definirFormulario((atual) => ({
        ...atual,
        semCadastro: false,
        nascimento: atual.nascimento || doCadastro.nascimento,
        sexo: atual.sexo || doCadastro.sexo,
        cep: atual.cep || doCadastro.cep,
        ...(convenioDoCadastro !== '' &&
        !convenioFixo(padroes) &&
        // No "Editar", o convênio é o que foi agendado: o cadastro não o troca.
        !remarcacao?.dados &&
        (atual.convenio === '' ||
          (atual.convenio === (padroes.convenio ?? '') && atual.plano === (padroes.plano ?? '')))
          ? { convenio: doCadastro.convenio, plano: doCadastro.plano }
          : {}),
        matricula: atual.matricula || doCadastro.matricula,
        // O celular de partida é o telefone do lead: o do cadastro, quando chega, vale mais.
        celular:
          atual.celular && atual.celular !== telefoneDoContato ? atual.celular : doCadastro.celular,
        whatsapp: atual.whatsapp || doCadastro.whatsapp,
        email: atual.email || doCadastro.email,
        endereco: atual.endereco || doCadastro.endereco,
        numero: atual.numero || doCadastro.numero,
        bairro: atual.bairro || doCadastro.bairro,
        cidade: atual.cidade || doCadastro.cidade,
        uf: atual.uf || doCadastro.uf,
        rg: atual.rg || doCadastro.rg,
        orgaoEmissor: atual.orgaoEmissor || doCadastro.orgaoEmissor,
      }));
    }
  }

  // Os planos dependem do convênio: o modelo os lê quando ele muda, inclusive no pré-preenchimento.
  useEffect(() => {
    aoEscolherConvenio(formulario.convenio);
  }, [formulario.convenio, aoEscolherConvenio]);

  const enviando = envio.situacao === 'enviando';
  const nomeDe = (lista: readonly OpcaoDaAgenda[], id: string) =>
    lista.find((opcao) => opcao.id === id)?.nome ?? '';
  const medicoDaVaga = confirmada ? nomeDe(medicos, confirmada.medicoId) : '';
  // O médico e a especialidade se filtram um pelo outro (#1187), pelo cadastro da clínica.
  const medicoEscolhido = medicos.find((m) => m.id === medicoId);
  const medicosDaLista = medicosDaEspecialidade(medicos, especialidadeId);
  const especialidadesDaLista = especialidadesDoMedico(especialidades, medicoEscolhido);
  const escolherEspecialidade = (id: string) => {
    definirEspecialidadeId(id);
    if (medicoEscolhido && id !== '' && !atende(medicoEscolhido, id)) definirMedicoId('');
  };
  const escolherMedico = (id: string) => {
    definirMedicoId(id);
    const medico = medicos.find((m) => m.id === id);
    if (!medico) return;
    const dele = especialidadesDoMedico(especialidades, medico);
    if (especialidadeId !== '' && !atende(medico, especialidadeId)) {
      definirEspecialidadeId(dele.length === 1 ? dele[0]!.id : '');
    } else if (especialidadeId === '' && dele.length === 1 && dele.length < especialidades.length) {
      definirEspecialidadeId(dele[0]!.id);
    }
  };
  // No passo 3, a especialidade se troca entre as do médico confirmado; sem a
  // lista do cadastro dele, fica a escolhida no passo 1.
  const medicoConfirmado = confirmada
    ? medicos.find((m) => m.id === confirmada.medicoId)
    : undefined;
  const doConfirmado = medicoConfirmado?.especialidades?.length
    ? especialidadesDoMedico(especialidades, medicoConfirmado)
    : [];
  // A escolhida sempre está na lista (no "Editar", pode não ser mais do cadastro dele).
  const escolhidaNoPasso3 = confirmada
    ? especialidades.find((e) => e.id === confirmada.especialidadeId)
    : undefined;
  const especialidadesDoConfirmado =
    escolhidaNoPasso3 && doConfirmado.length > 0 && !doConfirmado.includes(escolhidaNoPasso3)
      ? [escolhidaNoPasso3, ...doConfirmado]
      : doConfirmado;
  const trocarEspecialidade = (id: string) => {
    if (!confirmada || id === '' || id === confirmada.especialidadeId) return;
    definirEspecialidadeId(id);
    definirConfirmada({ ...confirmada, especialidadeId: id });
    aoTrocarEspecialidade(id);
  };
  const especialidadeDaVaga = confirmada ? nomeDe(especialidades, confirmada.especialidadeId) : '';
  // O horário de agora, no "Editar": vale como vaga, mesmo fora das livres.
  const vagaDeAgora: VagaLivre | null =
    remarcacao && remarcacao.consultaEm
      ? {
          id: remarcacao.vagaAtual,
          dia: '',
          hora: '',
          quando: instantePorExtenso(remarcacao.consultaEm),
          quandoPorExtenso: instantePorExtenso(remarcacao.consultaEm),
        }
      : null;
  const podeManter =
    vagaDeAgora !== null && confirmada !== null && confirmada.medicoId === remarcacao?.medicoId;
  const vaga =
    podeManter && vagaId === vagaDeAgora.id
      ? vagaDeAgora
      : vagas.situacao === 'pronta'
        ? (vagas.lista.find((v) => v.id === vagaId) ?? null)
        : null;
  const etapa2Aberta = confirmada !== null && !alterando;
  const etapa3Aberta = etapa2Aberta && vaga !== null && vagaConfirmada;

  const temPaciente = paciente.situacao === 'vinculado';
  const erros = errosDoFormulario(formulario, { temPaciente, hoje });
  const pronto = Object.keys(erros).length === 0;
  const nomeDoPaciente =
    paciente.situacao === 'vinculado'
      ? paciente.paciente.nome
      : formulario.semCadastro
        ? formulario.nome.trim()
        : '';

  const verVagas = () => {
    if (confirmada?.medicoId !== medicoId) {
      definirVagaId(null);
      definirVagaConfirmada(false);
    }
    const escolha = { especialidadeId, medicoId };
    definirConfirmada(escolha);
    definirAlterando(false);
    aoVerVagas(escolha);
  };
  const escolherVaga = (id: string) => {
    if (!enviando) definirVagaId(id);
  };
  const agendar = () => {
    definirTentou(true);
    if (vaga && pronto && !enviando) aoMarcar(vaga.id, formulario);
  };

  const titulo = (numero: 1 | 2 | 3, nome: string, situacao: 'atual' | 'feita' | 'espera') => (
    <div className="nova-consulta__cab-etapa">
      <span className="nova-consulta__numero" aria-hidden="true">
        {situacao === 'feita' ? <Icone nome="check" /> : numero}
      </span>
      <h4 className="nova-consulta__titulo-etapa" id={`nova-consulta-etapa-${numero}`}>
        {nome}
      </h4>
    </div>
  );

  const cancelar = (rotulo: string, aoClicar: () => void) => (
    <Botao
      variante="fantasma"
      miudo={forma === 'cartoes'}
      desabilitado={enviando}
      motivo={ESPERA_DO_SISAC}
      onClick={aoClicar}
    >
      {rotulo}
    </Botao>
  );

  const etapa1 = (
    <>
      {noComeco}
      {medicos.length === 0 || especialidades.length === 0 ? (
        <p className="nova-consulta__nota" role="status">
          A lista de médicos e especialidades vem do SISAC e ainda está vazia. Ela aparece aqui
          assim que a integração for ligada.
        </p>
      ) : (
        <p className="nova-consulta__apoio">
          Escolha a especialidade e o médico. A lista vem do SISAC.
        </p>
      )}
      <form
        className="nova-consulta__formulario"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (medicoId && especialidadeId) verVagas();
        }}
      >
        <div className="nova-consulta__dupla">
          <CampoEscolha
            id="nova-consulta-especialidade"
            rotulo="Especialidade"
            buscavel
            vazio="Escolha"
            valor={especialidadeId}
            opcoes={especialidadesDaLista.map((opcao) => ({ valor: opcao.id, rotulo: opcao.nome }))}
            aoEscolher={escolherEspecialidade}
          />
          <CampoEscolha
            id="nova-consulta-medico"
            rotulo="Médico"
            buscavel
            vazio="Escolha"
            valor={medicoId}
            opcoes={medicosDaLista.map((opcao) => ({ valor: opcao.id, rotulo: opcao.nome }))}
            aoEscolher={escolherMedico}
          />
        </div>
        {medicoId && especialidadeId && <ValorDaConsulta medicoId={medicoId} />}
        <div className="nova-consulta__acoes">
          {forma === 'passos' && cancelar('Cancelar', aoCancelar)}
          <Botao
            variante="primario"
            type="submit"
            desabilitado={!medicoId || !especialidadeId}
            motivo="Escolha a especialidade e o médico."
          >
            Ver horários
          </Botao>
        </div>
      </form>
    </>
  );

  const etapa2 = (
    <>
      {podeManter && (
        <div className="nova-consulta__manter">
          <p className="nova-consulta__nota">
            Horário de agora: <strong>{vagaDeAgora.quandoPorExtenso}</strong>
          </p>
          <Botao
            variante="secundario"
            desabilitado={enviando}
            motivo={ESPERA_DO_SISAC}
            onClick={() => {
              definirVagaId(vagaDeAgora.id);
              definirVagaConfirmada(true);
            }}
          >
            Manter o horário atual
          </Botao>
          <p className="nova-consulta__apoio">Ou escolha outro dia e horário abaixo.</p>
        </div>
      )}
      {ocupada && (
        <Faixa
          tom="bloqueado"
          acao={{ rotulo: 'Recarregar horários', aoClicar: aoRecarregarVagas }}
        >
          Este horário já foi ocupado. Recarregue os horários.
        </Faixa>
      )}
      {vagas.situacao === 'carregando' ? (
        <Carregando forma="lista" linhas={3} rotulo="Buscando os horários no SISAC" />
      ) : vagas.situacao === 'erro' ? (
        <div className="nova-consulta__vazio">
          <p className="nova-consulta__erro" role="alert">
            {vagas.recado}
          </p>
          <Botao variante="secundario" onClick={aoRecarregarVagas}>
            Tentar de novo
          </Botao>
        </div>
      ) : !porMes && vagas.lista.length === 0 ? (
        <div className="nova-consulta__vazio">
          <Icone nome="calendario" />
          <p className="nova-consulta__nota">
            Nenhum horário disponível para {medicoDaVaga} nos próximos {diasDeVagas} dias.
          </p>
          <Botao variante="secundario" onClick={() => definirAlterando(true)}>
            Trocar médico
          </Botao>
        </div>
      ) : (
        <>
          <CalendarioDeVagas
            rotulo={`Horários disponíveis de ${medicoDaVaga}`}
            vagas={vagas.lista}
            paginado={porMes ?? undefined}
            escolhida={vagaId}
            aoEscolher={escolherVaga}
            motivoTravado={enviando ? ESPERA_DO_SISAC : undefined}
          />
          {vagas.cortada && (
            <p className="nova-consulta__apoio">
              A lista mostra os primeiros horários disponíveis. Há outros mais adiante.
            </p>
          )}
          {!ocupada && (
            <div>
              <Botao
                variante="fantasma"
                miudo
                desabilitado={enviando}
                motivo={ESPERA_DO_SISAC}
                onClick={aoRecarregarVagas}
              >
                <Icone nome="repetir" />
                Recarregar horários
              </Botao>
            </div>
          )}
        </>
      )}
      <div className="nova-consulta__acoes">
        {forma === 'passos' && cancelar('Voltar', () => definirAlterando(true))}
        <Botao
          variante="primario"
          desabilitado={vaga === null}
          motivo="Escolha o dia e o horário."
          onClick={() => definirVagaConfirmada(true)}
        >
          Continuar
        </Botao>
      </div>
    </>
  );

  const etapa3 = (
    <>
      <DadosDoAgendamento
        formulario={formulario}
        erros={tentou ? erros : {}}
        listas={listas}
        paciente={paciente}
        buscaDoPaciente={buscaDoPaciente}
        especialidade={especialidadeDaVaga}
        especialidades={{
          valor: confirmada?.especialidadeId ?? '',
          opcoes: especialidadesDoConfirmado,
          aoEscolher: trocarEspecialidade,
        }}
        hoje={hoje}
        travado={enviando}
        ocultos={padroes.ocultos ?? []}
        aoMudar={mudar}
      />

      <div className="nova-consulta__final">
        {envio.situacao === 'fora-do-ar' && (
          <Faixa tom="bloqueado" acao={{ rotulo: 'Tentar de novo', aoClicar: agendar }}>
            O SISAC não respondeu. Nada foi agendado. Tente de novo em instantes.
          </Faixa>
        )}
        {envio.situacao === 'conflito' && (
          <p className="nova-consulta__erro" role="alert">
            Outra pessoa mudou esta consulta enquanto você agendava. Nada foi agendado. Cancele e
            abra a consulta de novo.
          </p>
        )}
        {envio.situacao === 'erro' && (
          <p className="nova-consulta__erro" role="alert">
            {envio.recado}
          </p>
        )}
        {tentou && !pronto && (
          <p className="nova-consulta__erro" role="alert">
            Preencha os campos marcados para agendar.
          </p>
        )}
        <div className="nova-consulta__linha-final">
          <p id="nova-consulta-resumo" className="nova-consulta__resumo">
            {[nomeDoPaciente || 'Paciente a escolher', medicoDaVaga, vaga?.quandoPorExtenso]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {forma === 'passos' && cancelar('Voltar', () => definirVagaConfirmada(false))}
          <Botao
            variante="primario"
            desabilitado={enviando}
            motivo={ESPERA_DO_SISAC}
            aria-describedby="nova-consulta-resumo"
            aria-busy={enviando || undefined}
            onClick={agendar}
          >
            {remarcacao
              ? enviando
                ? 'Salvando…'
                : 'Salvar alterações'
              : enviando
                ? 'Agendando…'
                : 'Agendar consulta'}
          </Botao>
        </div>
      </div>
    </>
  );

  if (forma === 'passos') {
    const passo = etapa3Aberta ? 3 : etapa2Aberta ? 2 : 1;
    const passos = [
      { numero: 1, nome: 'Médico', ir: () => definirAlterando(true) },
      { numero: 2, nome: 'Dia e horário', ir: () => definirVagaConfirmada(false) },
      { numero: 3, nome: 'Dados', ir: () => undefined },
    ] as const;
    return (
      <div className="nova-consulta nova-consulta--passos">
        <ol className="nova-consulta__trilha" aria-label="Etapas do agendamento">
          {passos.map(({ numero, nome, ir }) => {
            const situacao = numero < passo ? 'feita' : numero === passo ? 'atual' : 'espera';
            return (
              <li
                key={numero}
                className={`nova-consulta__marco nova-consulta__marco--${situacao}`}
                aria-current={situacao === 'atual' ? 'step' : undefined}
              >
                {situacao === 'feita' ? (
                  <button
                    type="button"
                    className="nova-consulta__marco-botao"
                    disabled={enviando}
                    title={enviando ? ESPERA_DO_SISAC : `Voltar para ${nome.toLowerCase()}`}
                    onClick={ir}
                  >
                    <span className="nova-consulta__numero" aria-hidden="true">
                      <Icone nome="check" />
                    </span>
                    {nome}
                  </button>
                ) : (
                  <span className="nova-consulta__marco-botao">
                    <span className="nova-consulta__numero" aria-hidden="true">
                      {numero}
                    </span>
                    {nome}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
        {passo > 1 && (
          <p className="nova-consulta__escolhas">
            {[
              `${medicoDaVaga} · ${especialidadeDaVaga}`,
              passo === 3 ? vaga?.quandoPorExtenso : null,
            ]
              .filter(Boolean)
              .join(' — ')}
          </p>
        )}
        {/* A chave reinicia a entrada a cada troca de etapa: a nova chega deslizando. */}
        <section
          key={passo}
          className="nova-consulta__passo"
          aria-labelledby={`nova-consulta-etapa-${passo}`}
        >
          {titulo(
            passo,
            ['Médico e especialidade', 'Dia e horário', 'Dados do agendamento'][passo - 1] ?? '',
            'atual',
          )}
          {passo === 1 ? etapa1 : passo === 2 ? etapa2 : etapa3}
        </section>
      </div>
    );
  }

  return (
    <div className="nova-consulta">
      <div className="nova-consulta__topo">
        <h3 className="nova-consulta__titulo">
          {remarcacao ? 'Editar consulta' : 'Nova consulta'}
        </h3>
        {cancelar('Cancelar', aoCancelar)}
      </div>

      {etapa2Aberta ? (
        <section
          className="nova-consulta__etapa nova-consulta__etapa--feita"
          aria-labelledby="nova-consulta-etapa-1"
        >
          <div className="nova-consulta__cab-etapa">
            <span className="nova-consulta__numero" aria-hidden="true">
              <Icone nome="check" />
            </span>
            <h4 className="nova-consulta__titulo-etapa" id="nova-consulta-etapa-1">
              Médico e especialidade
            </h4>
            <span className="nova-consulta__valor">
              {medicoDaVaga} · {especialidadeDaVaga}
              <Botao
                variante="fantasma"
                miudo
                desabilitado={enviando}
                motivo={ESPERA_DO_SISAC}
                onClick={() => definirAlterando(true)}
              >
                Alterar
              </Botao>
            </span>
          </div>
        </section>
      ) : (
        <section className="nova-consulta__etapa" aria-labelledby="nova-consulta-etapa-1">
          {titulo(1, 'Médico e especialidade', 'atual')}
          {etapa1}
        </section>
      )}

      {!etapa2Aberta ? (
        <section
          className="nova-consulta__etapa nova-consulta__etapa--espera"
          aria-labelledby="nova-consulta-etapa-2"
        >
          {titulo(2, 'Dia e horário', 'espera')}
          <p className="nova-consulta__apoio">Escolha o médico para ver os dias livres dele.</p>
        </section>
      ) : etapa3Aberta ? (
        <section
          className="nova-consulta__etapa nova-consulta__etapa--feita"
          aria-labelledby="nova-consulta-etapa-2"
        >
          <div className="nova-consulta__cab-etapa">
            <span className="nova-consulta__numero" aria-hidden="true">
              <Icone nome="check" />
            </span>
            <h4 className="nova-consulta__titulo-etapa" id="nova-consulta-etapa-2">
              Dia e horário
            </h4>
            <span className="nova-consulta__valor">
              {vaga?.quandoPorExtenso}
              <Botao
                variante="fantasma"
                miudo
                desabilitado={enviando}
                motivo={ESPERA_DO_SISAC}
                onClick={() => definirVagaConfirmada(false)}
              >
                Alterar
              </Botao>
            </span>
          </div>
        </section>
      ) : (
        <section className="nova-consulta__etapa" aria-labelledby="nova-consulta-etapa-2">
          {titulo(2, 'Dia e horário', 'atual')}
          {etapa2}
        </section>
      )}

      {!etapa3Aberta ? (
        <section
          className="nova-consulta__etapa nova-consulta__etapa--espera"
          aria-labelledby="nova-consulta-etapa-3"
        >
          {titulo(3, 'Dados do agendamento', 'espera')}
          <p className="nova-consulta__apoio">
            Depois do horário, o paciente e os dados que o SISAC pede.
          </p>
        </section>
      ) : (
        <section className="nova-consulta__etapa" aria-labelledby="nova-consulta-etapa-3">
          {titulo(3, 'Dados do agendamento', 'atual')}
          {etapa3}
        </section>
      )}
    </div>
  );
}
